// SEO landing page for a single catalog agent (/agents/<slug>).
// Public JSON + optional auth mirrors task templates and tools; long-form
// body uses ToolPageCopy from `meta.pageCopy` when present.
//
// Header (sidebar + back + “Use this Agent”): `getAgentCatalogDetailRouteNavigationOptions`
// in `AgentCatalogDetailRouteNavigationOptions.jsx`.
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Platform,
  Pressable,
  ActivityIndicator,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import useStartAgentWorkChat from '@/hooks/useStartAgentWorkChat';
import MetaHead from '@/components/MetaHead';
import CatalogDetailHero from '@/components/Catalog/CatalogDetailHero';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import ToolPageCopy from '@/components/Tools/ToolPageCopy';
import AgentCard from '@/components/AgentCard';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import { useAgentStore } from '@/store';
import { agentApiSsr } from '@/lib/agentApi';
import { BORDER_RADIUS } from '@/constants/config';
import {
  colorForSlug,
  heroChromaForSlug,
  formatCount,
  primaryOutputKind,
  outputKindIcon,
} from '@/utils/agent_catalog';

const _staticAgentCache = {};

export async function generateStaticParams() {
  // Rendered on demand (client SPA), not prerendered. Returning [] keeps
  // `expo export` fast and avoids hammering the API at build time.
  return [];
}

function normalizePageCopy(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((b) => {
        if (!b || typeof b !== 'object') return null;
        const type = String(b.type || '').trim();
        if (!type) return null;
        return { ...b, type };
      })
      .filter(Boolean);
  }
  if (typeof raw === 'object') {
    const blocks = [];
    if (raw.hero && typeof raw.hero === 'object') {
      blocks.push({ type: 'hero', ...raw.hero });
    }
    if (Array.isArray(raw.sections)) {
      raw.sections.forEach((s) => {
        if (s && typeof s === 'object') blocks.push(s);
      });
    }
    return blocks;
  }
  return [];
}

function buildFaqBlock(faq) {
  if (!Array.isArray(faq) || !faq.length) return null;
  const items = faq
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const q = String(row.q || row.question || '').trim();
      const a = String(row.a || row.answer || '').trim();
      if (!q || !a) return null;
      return { q, a };
    })
    .filter(Boolean);
  if (!items.length) return null;
  return { type: 'faq', title: 'FAQ', items };
}

const createStyles = (
  colors,
  isXS,
  isSM,
  isXL,
  insetBottom,
  accent,
  heroChroma,
  cardWidth,
) => {
  const stacked = isXS || isSM;
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.solid_color_primary },
    scroll: { flex: 1, width: '100%' },
    scrollContent: { width: '100%', paddingBottom: insetBottom + 100 },
    fullBleedBand: {
      width: '100%',
      alignSelf: 'stretch',
      paddingHorizontal: 0,
      marginBottom: 18,
    },
    inner: {
      width: '100%',
      maxWidth: isXL ? 1100 : 960,
      alignSelf: 'center',
      paddingHorizontal: isXS ? 16 : 28,
      paddingTop: 0,
      gap: 28,
    },

    // "What is an AI agent?" + "Capabilities" — side-by-side on desktop.
    featureRow: {
      flexDirection: stacked ? 'column' : 'row',
      gap: stacked ? 14 : 18,
      alignItems: 'stretch',
    },

    explainerCard: {
      borderRadius: isXS ? BORDER_RADIUS.large : BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      padding: isXS ? 18 : 22,
      gap: 12,
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      position: 'relative',
    },
    explainerHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },
    explainerIconWrap: {
      width: 44,
      height: 44,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: `${accent}1f`,
      borderWidth: 1,
      borderColor: `${accent}33`,
    },
    explainerEyebrow: {
      textTransform: 'uppercase',
      letterSpacing: 1.4,
      fontSize: 11,
      fontWeight: '800',
      marginBottom: 2,
    },
    explainerFeatures: {
      gap: 10,
      marginTop: 6,
    },
    explainerFeatureRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 10,
    },
    explainerFeatureIcon: {
      width: 28,
      height: 28,
      borderRadius: 9,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: `${accent}1a`,
      marginTop: 1,
    },

    previewFrame: {
      borderRadius: isXS ? BORDER_RADIUS.large : BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      overflow: 'hidden',
      backgroundColor: colors.solid_color_block,
      flex: 1,
      minWidth: 0,
    },
    previewAccentBar: { height: 4, width: '100%' },
    previewInner: { padding: isXS ? 18 : 22, gap: 14 },
    previewHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },
    previewIconWrap: {
      width: 36,
      height: 36,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: `${accent}1f`,
      borderWidth: 1,
      borderColor: `${accent}33`,
    },
    sectionLabel: {
      fontSize: 11,
      fontWeight: '800',
      letterSpacing: 1.3,
      textTransform: 'uppercase',
      opacity: 0.6,
    },
    statPills: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    statPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingVertical: 6,
      paddingHorizontal: 11,
      borderRadius: BORDER_RADIUS.round,
      borderWidth: 1,
      borderColor: `${accent}33`,
      backgroundColor: `${accent}10`,
    },

    // Slim, clickable starter prompt rows. Mirrors the hover/affordance
    // language of `ConversationStarterItem` (border tint + arrow icon on
    // hover) so users know clicking opens the chat with that agent.
    promptList: { gap: 8 },
    promptRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingVertical: 9,
      paddingHorizontal: 12,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_tertiary,
      ...(Platform.OS === 'web'
        ? {
            cursor: 'pointer',
            transition: 'border-color 150ms ease, background-color 150ms ease',
          }
        : null),
    },
    promptRowHover: {
      borderColor: `${accent}88`,
      backgroundColor: `${accent}12`,
    },
    promptQuoteWrap: {
      width: 22,
      height: 22,
      borderRadius: 7,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: `${accent}1f`,
    },
    promptArrowWrap: {
      width: 20,
      alignItems: 'center',
      justifyContent: 'center',
    },

    copySection: { marginTop: 8, gap: 8 },

    // "Ready to start?" — centered hero-language CTA. Uses the same
    // chroma values as `CatalogDetailHero` so the background tint reads
    // identically to the page header, and sits on `solid_color_block`
    // (matches the recipes detail page).
    bottomCta: {
      marginTop: 8,
      borderRadius: isXS ? BORDER_RADIUS.large : BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      overflow: 'hidden',
      position: 'relative',
    },
    bottomCtaWashSoft: { ...StyleSheet.absoluteFillObject },
    bottomCtaBlobA: {
      position: 'absolute',
      width: isXS ? 280 : 360,
      height: isXS ? 280 : 360,
      borderRadius: 400,
      top: isXS ? -100 : -118,
      right: isXS ? -72 : -88,
      backgroundColor: heroChroma.blobA,
      opacity: Platform.OS === 'web' ? 0.52 : 0.5,
      ...(Platform.OS === 'web' ? { filter: 'blur(44px)' } : null),
    },
    bottomCtaBlobB: {
      position: 'absolute',
      width: isXS ? 200 : 260,
      height: isXS ? 200 : 260,
      borderRadius: 300,
      bottom: isXS ? -110 : -130,
      left: isXS ? -70 : -90,
      backgroundColor: heroChroma.blobB,
      opacity: Platform.OS === 'web' ? 0.38 : 0.36,
      ...(Platform.OS === 'web' ? { filter: 'blur(36px)' } : null),
    },
    bottomCtaInner: {
      padding: isXS ? 28 : 44,
      gap: 12,
      alignItems: 'center',
    },
    bottomCtaBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingVertical: 5,
      paddingHorizontal: 11,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: `${accent}55`,
      backgroundColor: `${accent}1a`,
      marginBottom: 6,
    },
    bottomCtaTitle: {
      fontSize: isXS ? 28 : 38,
      lineHeight: isXS ? 32 : 44,
      letterSpacing: -0.6,
      fontWeight: '800',
      textAlign: 'center',
      marginBottom: 0,
    },
    bottomCtaBody: {
      fontSize: isXS ? 15 : 16,
      lineHeight: isXS ? 22 : 24,
      textAlign: 'center',
      maxWidth: 540,
      opacity: 0.78,
    },
    bottomCtaButton: {
      marginTop: 14,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 14,
      paddingHorizontal: 26,
      borderRadius: 999,
      backgroundColor: accent,
      ...(Platform.OS === 'web'
        ? {
            cursor: 'pointer',
            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
            transition: 'box-shadow 0.2s ease, background-color 0.15s ease',
          }
        : null),
    },

    // Related agents grid. Cells use a fixed `cardWidth` (calc on web,
    // percentage on native) so a partial last row doesn't stretch its
    // remaining cards wider than the cards above them.
    relatedSection: { marginTop: 8, gap: 14 },
    relatedHeader: {
      flexDirection: 'row',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 12,
    },
    relatedGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 10,
      alignItems: 'stretch',
    },
    relatedCell: {
      width: cardWidth,
      minWidth: 0,
      ...(Platform.OS === 'web' ? { display: 'flex', flexDirection: 'column' } : {}),
    },

    notFound: { paddingVertical: 48, alignItems: 'center', gap: 16 },
    loadingBox: {
      paddingVertical: 80,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 14,
    },
  });
};

export default function AgentSeoDetailPage() {
  const params = useLocalSearchParams();
  const slugRaw = params?.slug;
  const slug =
    typeof slugRaw === 'string'
      ? slugRaw
      : Array.isArray(slugRaw)
        ? slugRaw[0] ?? null
        : slugRaw ?? null;
  const slugNorm = slug ? String(slug).toLowerCase() : '';

  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS, isXL, isSM, isMD, showsScrollIndicator } = useResponsiveness();
  const insets = useSafeAreaInsets();

  const addAgents = useAgentStore((s) => s.addAgents);
  const agentsMap = useAgentStore((s) => s.agents);
  // Resolve the agent by slug from the cached map. Doing the scan inside a
  // `useMemo` (instead of inside the zustand selector) means we only iterate
  // when the agents map or slug actually change — not on every unrelated
  // store mutation, and not on every re-render.
  const storeAgentBySlug = useMemo(() => {
    if (!slugNorm || !agentsMap) return null;
    for (const key in agentsMap) {
      const a = agentsMap[key];
      if (a?.slug && String(a.slug).toLowerCase() === slugNorm) return a;
    }
    return null;
  }, [agentsMap, slugNorm]);

  const startAgentWorkChat = useStartAgentWorkChat();

  const [remoteAgent, setRemoteAgent] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loadingRemote, setLoadingRemote] = useState(false);
  /** Same-category agents from public category endpoint (store may be empty on cold landing). */
  const [categoryPeers, setCategoryPeers] = useState([]);

  const staticAgent = slug
    ? (_staticAgentCache[slug] || _staticAgentCache[String(slug).toLowerCase()])
    : null;

  useEffect(() => {
    let cancelled = false;
    if (!slug) return undefined;
    setLoadingRemote(true);
    setLoadError(null);
    (async () => {
      try {
        const fetched = await agentApiSsr.getBySlug(slug);
        if (cancelled) return;
        if (fetched?.id) {
          addAgents([fetched]);
          setRemoteAgent(fetched);
          setLoadError(null);
        } else {
          setRemoteAgent(null);
          setLoadError('notfound');
        }
      } catch (e) {
        if (!cancelled) {
          setRemoteAgent(null);
          setLoadError(e?.message || 'error');
        }
      } finally {
        if (!cancelled) setLoadingRemote(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, addAgents]);

  // Catalog agents in the store use `serialize()` without populated
  // `category`, so `category` is often a Mongo id string. Prefer the
  // `by-slug` payload once it arrives so `category.slug` is available and
  // we do not hit `/json/agent-categories/:slug/agents` with an ObjectId.
  const agent = useMemo(() => {
    const fromStore = storeAgentBySlug;
    const fromRemote = remoteAgent;
    const fromStatic = staticAgent;
    if (fromStore && fromRemote && String(fromStore.id) === String(fromRemote.id)) {
      return { ...fromStore, ...fromRemote };
    }
    return fromStore || fromRemote || fromStatic || null;
  }, [storeAgentBySlug, remoteAgent, staticAgent]);

  /** Public category slug only (never an unpopulated Mongo id from list payloads). */
  const resolvedCategorySlug = useMemo(() => {
    if (!agent) return '';
    const cat = agent.category;
    if (cat && typeof cat === 'object' && cat.slug) {
      return String(cat.slug).trim();
    }
    if (typeof cat === 'string') {
      const s = cat.trim();
      if (/^[a-f0-9]{24}$/i.test(s)) return '';
      return s;
    }
    return '';
  }, [agent]);

  useEffect(() => {
    let cancelled = false;
    if (!resolvedCategorySlug || !agent?.id) {
      setCategoryPeers([]);
      return undefined;
    }
    (async () => {
      try {
        const data = await agentApiSsr.getCategoryWithAgents(resolvedCategorySlug, {
          page: 1,
        });
        const list = Array.isArray(data?.agents) ? data.agents : [];
        if (cancelled) return;
        setCategoryPeers(list.filter((a) => a?.id && a.id !== agent.id));
      } catch {
        if (!cancelled) setCategoryPeers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resolvedCategorySlug, agent?.id]);

  const accent = useMemo(() => colorForSlug(resolvedCategorySlug), [resolvedCategorySlug]);
  const heroChroma = useMemo(
    () => heroChromaForSlug(resolvedCategorySlug),
    [resolvedCategorySlug],
  );
  // Related-agents grid columns. Mirrors `agents/index.jsx` so a card's
  // width here matches the catalog and partial last rows don't stretch
  // their remaining cards. With a 10px gap between cells.
  const gridCols = isXS ? 1 : isMD ? 2 : isXL ? 4 : 3;
  const cardWidth = Platform.OS === 'web'
    ? `calc((100% - ${(gridCols - 1) * 10}px) / ${gridCols})`
    : `${100 / gridCols}%`;

  const styles = useMemo(
    () => createStyles(colors, isXS, isSM, isXL, insets.bottom, accent, heroChroma, cardWidth),
    [colors, isXS, isSM, isXL, insets.bottom, accent, heroChroma, cardWidth],
  );

  const pageCopyBlocks = useMemo(() => {
    if (!agent) return [];
    const raw = agent.meta?.pageCopy;
    const normalized = normalizePageCopy(raw);
    const faqBlock = buildFaqBlock(agent.meta?.faq);
    return faqBlock ? [...normalized, faqBlock] : normalized;
  }, [agent]);

  const pageCopyPalette = useMemo(
    () => ({
      bg: `${accent}22`,
      border: `${accent}55`,
      text: colors.text_color_primary,
    }),
    [accent, colors.text_color_primary],
  );

  const relatedAgents = useMemo(() => {
    if (!agent?.id) return [];
    const merged = new Map();
    (categoryPeers || []).forEach((a) => {
      if (a?.id && a.id !== agent.id) merged.set(a.id, a);
    });
    if (resolvedCategorySlug) {
      Object.values(agentsMap || {}).forEach((a) => {
        if (
          a?.id &&
          a.id !== agent.id &&
          (a?.category?.slug === resolvedCategorySlug || a?.category === resolvedCategorySlug)
        ) {
          merged.set(a.id, a);
        }
      });
    }
    return [...merged.values()]
      .sort(
        (a, b) =>
          (b?.stats?.conversationCount || 0) - (a?.stats?.conversationCount || 0),
      )
      .slice(0, 16);
  }, [agent, agentsMap, resolvedCategorySlug, categoryPeers]);

  const useAgent = useCallback(() => {
    startAgentWorkChat(agent, { source: 'agent_detail_use_agent' });
  }, [agent, startAgentWorkChat]);

  const openRelatedAgent = useCallback(
    (a) => {
      if (!a) return;
      if (a.slug) router.push(`/agents/${encodeURIComponent(a.slug)}`);
      else startAgentWorkChat(a, { source: 'agent_related' });
    },
    [router, startAgentWorkChat],
  );

  const canonicalPath = `/agents/${encodeURIComponent(slug || '')}`;
  const metaTitle =
    agent?.meta?.title ||
    (agent ? `${agent.name} · ${t('pages.agents.title')}` : t('pages.agents.title'));
  const metaDescription =
    agent?.meta?.description ||
    agent?.description ||
    t('pages.agents.agentSeoMetaFallback');

  const outKind = agent ? primaryOutputKind(agent) : '';
  const convCount = agent?.stats?.conversationCount || 0;
  const prompts = Array.isArray(agent?.defaultPrompts) ? agent.defaultPrompts.filter(Boolean) : [];

  const catalogHeroMetaItems = useMemo(() => {
    const rows = [];
    if (outKind) rows.push({ icon: outputKindIcon(outKind), label: outKind });
    if (convCount > 0) {
      rows.push({
        icon: 'comments',
        label: `${formatCount(convCount)} ${t('pages.agents.conversationsLabel')}`,
      });
    }
    return rows;
  }, [outKind, convCount, t]);

  if (!slug) {
    return (
      <View style={styles.container}>
        <View style={styles.notFound}>
          <Text type="h2">{t('pages.agents.agentMissingSlug')}</Text>
        </View>
      </View>
    );
  }

  if (loadingRemote && !agent && !staticAgent) {
    return (
      <View style={[styles.container, styles.loadingBox]}>
        <MetaHead
          title={metaTitle}
          description={metaDescription}
          canonical={canonicalPath}
        />
        <ActivityIndicator size="large" color={accent} />
        <Text type="p" style={{ opacity: 0.7 }}>
          {t('pages.agents.agentDetailLoading')}
        </Text>
      </View>
    );
  }

  if (!agent && (loadError === 'notfound' || (!loadingRemote && !staticAgent))) {
    return (
      <View style={styles.container}>
        <MetaHead
          title={metaTitle}
          description={metaDescription}
          canonical={canonicalPath}
        />
        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
          <View style={styles.inner}>
            <View style={styles.notFound}>
              <Text type="h2">{t('pages.agents.agentNotFound')}</Text>
              <Text type="p" style={{ textAlign: 'center', opacity: 0.75 }}>
                {t('pages.agents.agentRemovedUnavailable')}
              </Text>
              <Button
                text={t('pages.agents.browseAgents')}
                onPress={() => router.push('/agents')}
              />
            </View>
          </View>
        </ScrollView>
      </View>
    );
  }

  if (!agent) {
    return (
      <View style={[styles.container, styles.loadingBox]}>
        <MetaHead
          title={metaTitle}
          description={metaDescription}
          canonical={canonicalPath}
        />
        <ActivityIndicator size="large" color={accent} />
      </View>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: agent.name,
    description: metaDescription,
    url: `https://gab.ai${canonicalPath}`,
  };

  return (
    <View style={styles.container}>
      <MetaHead
        title={metaTitle}
        description={metaDescription}
        canonical={canonicalPath}
        keywords={agent.meta?.keywords}
        structuredData={jsonLd}
        image={agent.meta?.ogImage || agent.avatarPath || undefined}
      />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={showsScrollIndicator}
      >
        <View style={styles.fullBleedBand}>
          <CatalogDetailHero
            backLink={{
              label: t('pages.agents.browseAgents'),
              onPress: () => router.push('/agents'),
            }}
            categorySlug={resolvedCategorySlug}
            catalogCategory={{
              label: agent?.category?.name || t('pages.agents.title'),
              icon: 'folder',
              ...(resolvedCategorySlug
                ? {
                    href: `/agents/category/${encodeURIComponent(resolvedCategorySlug)}`,
                    accessibilityLabel: `${agent?.category?.name || t('pages.agents.title')} category`,
                  }
                : {}),
            }}
            heroMaxWidth={1100}
            avatar={{
              uri: agent.avatarPath || null,
              invertImage: !!agent?.flags?.isImageInvertable,
            }}
            eyebrowBadge={agent.flags?.isPlusOnly ? 'plus' : null}
            headline={agent.name}
            subtitle={agent.description || null}
            primaryAction={{
              label: t('pages.agents.useThisAgent'),
              icon: 'comments',
              onPress: useAgent,
              accessibilityLabel: t('pages.agents.useThisAgent'),
            }}
            metaItems={catalogHeroMetaItems}
          />
        </View>

        <View style={styles.inner}>
          {/* "What is an AI agent?" + "Capabilities" — split 50/50 on
              desktop, stacked on small screens. Both cards stretch to
              equal height because the parent uses `alignItems: 'stretch'`
              and each child sets `flex: 1`. */}
          <View style={styles.featureRow}>
            <View style={styles.explainerCard}>
              <View style={styles.explainerHeaderRow}>
                <View style={styles.explainerIconWrap}>
                  <Icon name="robot" size={22} color={accent} solid />
                </View>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={[styles.explainerEyebrow, { color: accent }]}>
                    Why agents
                  </Text>
                  <Text type="h3" style={{ marginBottom: 0 }}>
                    {t('pages.agents.whatIsAgentTitle')}
                  </Text>
                </View>
              </View>
              <Text type="p" style={{ lineHeight: 22, opacity: 0.88 }}>
                {t('pages.agents.whatIsAgentBody')}
              </Text>

              <View style={styles.explainerFeatures}>
                <View style={styles.explainerFeatureRow}>
                  <View style={styles.explainerFeatureIcon}>
                    <Icon name="bullseye" size={12} color={accent} solid />
                  </View>
                  <Text size="s" color="secondary" style={{ flex: 1, lineHeight: 20 }}>
                    <Text size="s" weight="bold" color="primary">Focused expertise</Text>
                    {' — primed for a specific job so answers stay sharp and on-topic.'}
                  </Text>
                </View>
                <View style={styles.explainerFeatureRow}>
                  <View style={styles.explainerFeatureIcon}>
                    <Icon name="bolt" size={12} color={accent} solid />
                  </View>
                  <Text size="s" color="secondary" style={{ flex: 1, lineHeight: 20 }}>
                    <Text size="s" weight="bold" color="primary">Plug-and-play</Text>
                    {' — start with saved instructions, then add your context and goal.'}
                  </Text>
                </View>
                <View style={styles.explainerFeatureRow}>
                  <View style={styles.explainerFeatureIcon}>
                    <Icon name="layer-group" size={12} color={accent} solid />
                  </View>
                  <Text size="s" color="secondary" style={{ flex: 1, lineHeight: 20 }}>
                    <Text size="s" weight="bold" color="primary">Multi-modal</Text>
                    {' — available outputs depend on the bot, mode, and plan.'}
                  </Text>
                </View>
              </View>
            </View>

            <View style={styles.previewFrame}>
              <LinearGradient
                colors={[accent, `${accent}99`]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={styles.previewAccentBar}
              />
              <View style={styles.previewInner}>
                <View style={styles.previewHeaderRow}>
                  <View style={styles.previewIconWrap}>
                    <Icon name="wand-magic-sparkles" size={16} color={accent} solid />
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={[styles.explainerEyebrow, { color: accent }]}>
                      {t('pages.agents.capabilitiesEyebrow')}
                    </Text>
                    <Text type="h3" style={{ marginBottom: 0 }}>
                      What it can do
                    </Text>
                  </View>
                </View>

                {((agent.inputTypes || []).length || (agent.outputTypes || []).length) ? (
                  <View style={styles.statPills}>
                    {(agent.inputTypes || []).length ? (
                      <View style={styles.statPill}>
                        <Icon name="arrow-right-to-bracket" size={11} color={accent} solid />
                        <Text size="s" weight="bold" color="primary">
                          {t('pages.agents.inputsLabel')}: {(agent.inputTypes || []).join(', ')}
                        </Text>
                      </View>
                    ) : null}
                    {(agent.outputTypes || []).length ? (
                      <View style={styles.statPill}>
                        <Icon name="arrow-right-from-bracket" size={11} color={accent} solid />
                        <Text size="s" weight="bold" color="primary">
                          {t('pages.agents.outputsLabel')}: {(agent.outputTypes || []).join(', ')}
                        </Text>
                      </View>
                    ) : null}
                  </View>
                ) : null}

                {prompts.length ? (
                  <>
                    <Text
                      style={[
                        styles.sectionLabel,
                        { color: colors.text_color_secondary, marginTop: 4 },
                      ]}
                    >
                      {t('pages.agents.starterPromptsTitle')}
                    </Text>
                    <View style={styles.promptList}>
                      {prompts.slice(0, 4).map((p, idx) => (
                        <Pressable
                          key={idx}
                          onPress={useAgent}
                          accessibilityRole="button"
                          accessibilityLabel={`Start chat: ${p}`}
                          style={({ hovered }) => [
                            styles.promptRow,
                            hovered && Platform.OS === 'web' && styles.promptRowHover,
                          ]}
                        >
                          {({ hovered }) => {
                            const isHover = hovered && Platform.OS === 'web';
                            return (
                              <>
                                <View style={styles.promptQuoteWrap}>
                                  <Icon name="quote-left" size={10} color={accent} solid />
                                </View>
                                <Text
                                  size="s"
                                  color="primary"
                                  numberOfLines={2}
                                  style={{
                                    flex: 1,
                                    lineHeight: 18,
                                    textDecorationLine: isHover ? 'underline' : 'none',
                                  }}
                                >
                                  {p}
                                </Text>
                                <View style={styles.promptArrowWrap}>
                                  <Icon
                                    name="arrow-up-right"
                                    size={12}
                                    color={isHover ? accent : colors.text_color_tertiary}
                                    solid
                                  />
                                </View>
                              </>
                            );
                          }}
                        </Pressable>
                      ))}
                    </View>
                  </>
                ) : null}
              </View>
            </View>
          </View>

          {pageCopyBlocks.length ? (
            <View style={styles.copySection}>
              <ToolPageCopy
                blocks={pageCopyBlocks}
                palette={pageCopyPalette}
                isMD={isSM || isMD}
              />
            </View>
          ) : agent.bio ? (
            <View style={styles.explainerCard}>
              <Text type="h3" style={{ marginBottom: 8 }}>
                {t('pages.agents.aboutAgent')}
              </Text>
              <Text type="p" style={{ lineHeight: 22, opacity: 0.88 }}>
                {agent.bio}
              </Text>
            </View>
          ) : null}

          {/* "Ready to start?" — centered CTA on a soft accent wash. The
              gradient + blob chroma values match `CatalogDetailHero` so
              this card reads as the same color family as the page hero
              (not a darker block). */}
          <View style={styles.bottomCta}>
            <LinearGradient
              colors={[colors.solid_color_tertiary, 'transparent']}
              locations={[0, 1]}
              start={{ x: 0.5, y: 1 }}
              end={{ x: 0.5, y: 0.2 }}
              style={styles.bottomCtaWashSoft}
              pointerEvents="none"
            />
            <LinearGradient
              colors={[heroChroma.gradSoft, heroChroma.gradMid, 'transparent']}
              locations={[0, 0.2, 1]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.bottomCtaWashSoft}
              pointerEvents="none"
            />
            <View style={styles.bottomCtaBlobA} pointerEvents="none" />
            <View style={styles.bottomCtaBlobB} pointerEvents="none" />
            <View style={styles.bottomCtaInner}>
              <View style={styles.bottomCtaBadge}>
                <Icon name="sparkles" size={11} color={accent} solid />
                <Text
                  size="s"
                  weight="extraBold"
                  style={{ color: accent, letterSpacing: 0.6 }}
                >
                  {(agent?.category?.name || 'Super Intelligence Bot').toUpperCase()}
                </Text>
              </View>
              <Text tag="h2" color="primary" style={styles.bottomCtaTitle}>
                {t('pages.agents.readyToChatTitle')}
              </Text>
              <Text color="secondary" style={styles.bottomCtaBody}>
                {t('pages.agents.readyToChatBody')}
              </Text>
              <Pressable
                onPress={useAgent}
                accessibilityRole="button"
                accessibilityLabel={t('pages.agents.useThisAgent')}
                style={({ hovered, pressed }) => [
                  styles.bottomCtaButton,
                  // Avoid `transform: translateY` on hover — RN Web can
                  // flicker its hover state on the shifted bounds and
                  // trip React's "Maximum update depth" guard.
                  Platform.OS === 'web' && hovered && {
                    boxShadow: '0 10px 28px rgba(0,0,0,0.18)',
                  },
                  pressed && { opacity: 0.92 },
                ]}
              >
                <Icon name="comments" size={16} color="#fff" solid />
                <Text size="m" weight="bold" style={{ color: '#fff' }}>
                  {t('pages.agents.useThisAgent')}
                </Text>
                <Icon name="arrow-right" size={12} color="#fff" solid />
              </Pressable>
            </View>
          </View>

          {/* Related agents — responsive grid with equal-height cards.
              AgentCard already supports stretch height + a bottom-docked
              CTA via `alignSelf: 'stretch'` / `ctaSpacer`, so cards in
              the same row line up cleanly even when descriptions differ
              in length. */}
          {relatedAgents.length ? (
            <View style={styles.relatedSection}>
              <View style={styles.relatedHeader}>
                <Text type="h3" style={{ marginBottom: 0 }}>
                  {t('pages.agents.sameCategoryAgents')}
                </Text>
                <Text size="s" color="secondary" style={{ opacity: 0.7 }}>
                  {agent?.category?.name || ''}
                </Text>
              </View>
              <View style={styles.relatedGrid}>
                {relatedAgents.map((rel) => (
                  <View key={rel.id} style={styles.relatedCell}>
                    <AgentCard agent={rel} onPress={openRelatedAgent} />
                  </View>
                ))}
              </View>
            </View>
          ) : null}
        </View>

        <AppDashboardFooter fullViewportWidth />
      </ScrollView>
    </View>
  );
}
